import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRuntimeConfig } from "#imports";

export const createOrganizationSchema = (t, baseUrl) => ({
  "@type": "Organization",
  "@id": `${baseUrl}/#organization`,
  name: "Tradeview Europe",
  legalName: "Tradeview Europe Ltd.",
  identifier: "C93990",
  url: `${baseUrl}/`,
  logo: `${baseUrl}/images/logo-main.svg`,
  description: t("shema.description"),
  address: {
    "@type": "PostalAddress",
    streetAddress: "Floor 5, The Ferries Business Centre, Sqaq Ġuzi Fava",
    addressLocality: "Sliema",
    postalCode: "SLM1632",
    addressCountry: "MT",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+35620311017",
    email: "support@tradeview.eu",
    contactType: t("shema.contactPoint.contactType"),
    areaServed: "EU/EEA",
    availableLanguage: ["en", "es", "it", "fr", "pt", "pl"],
  },
});

export const createWebSiteSchema = (baseUrl) => ({
  "@type": "WebSite",
  "@id": `${baseUrl}/#website`,
  url: `${baseUrl}/`,
  name: "Tradeview Europe",
  publisher: { "@id": `${baseUrl}/#organization` },
  inLanguage: ["en", "es", "it", "fr", "pt", "pl"],
});

const generatePageSchema = (locale, path, t, baseUrl) => {
  const organizationId = `${baseUrl}/#organization`;
  const graph = [];

  if (path === "/" || path === "/company/about-us/" || path === "/faq/") {
    graph.push(createOrganizationSchema(t, baseUrl));
  }

  if (path === "/" || path === "/faq/") {
    graph.push(createWebSiteSchema(baseUrl));
  }

  if (path === "/faq/") {
    const url = `${baseUrl}/${locale}/faq/`;
    graph.push({
      "@type": ["WebPage", "CollectionPage"],
      "@id": `${url}#webpage`,
      url,
      name: t("seo.faq.title"),
      inLanguage: locale,
      isPartOf: { "@id": `${baseUrl}/#website` },
      publisher: { "@id": organizationId },
    });
  }

  if (path === "/trading/markets/forex/" || path === "/trading/markets/stocks/") {
    const market = path === "/trading/markets/forex/" ? "forex" : "stocks";
    const url = `${baseUrl}/${locale}${path}`;

    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: t(`shema.products.${market}.name`),
      description: t(`shema.products.${market}.description`),
      provider: { "@id": organizationId },
      url,
      category: t(`shema.products.${market}.category`),
    });
  }

  return graph;
};

export const usePageSchema = () => {
  const { locale, t } = useI18n();
  const route = useRoute();
  const baseUrl = useRuntimeConfig().public.siteUrl;

  return computed(() => {
    const localePrefix = `/${locale.value}`;
    const routePath = route.path.startsWith(`${localePrefix}/`)
      ? route.path.slice(localePrefix.length)
      : route.path === localePrefix ? "/" : route.path;
    const path = routePath.endsWith("/") ? routePath : `${routePath}/`;

    return {
      "@context": "https://schema.org",
      "@graph": generatePageSchema(locale.value, path, t, baseUrl),
    };
  });
};
